"use client";

import { useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";

const SUPPORTED_COUNTRIES: Record<string, string> = {
  US: "us",
  GB: "gb",
  AU: "au",
  CA: "ca",
  SG: "sg",
  IN: "in",
  NZ: "nz",
  DE: "de",
  CH: "ch",
  NL: "nl",
};

const STORAGE_KEY = "selectedCountry";
const GEO_API_URL = "https://ipapi.co/json/";

/**
 * Hook that auto-detects the user's country via IP geolocation
 * and redirects them from the homepage to their country dashboard.
 *
 * - Only triggers on the homepage ("/")
 * - Caches the result in localStorage to avoid repeat API calls
 * - Respects manual country selection (stored under the same key)
 */
function useGeoRedirect() {
  const router = useRouter();
  const pathname = usePathname();
  const attempted = useRef(false);

  useEffect(() => {
    // Only redirect from the homepage
    if (pathname !== "/") return;

    // Detect bots/crawlers - don't redirect them
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
      const userAgent = navigator.userAgent.toLowerCase();
      const isBotOrCrawler = /bot|crawler|spider|crawling|google|mediapartners|adsbot|headless|prerender/i.test(userAgent);
      if (isBotOrCrawler) {
        return; // Don't redirect bots
      }
    }

    // Check if we already redirected in this session to allow manual back-navigation
    if (typeof window !== "undefined" && sessionStorage.getItem("geo_redirected") === "true") {
      return;
    }

    // Prevent double-fire in React strict mode
    if (attempted.current) return;
    attempted.current = true;

    const performRedirect = (code: string) => {
      try {
        if (typeof window !== "undefined") {
          sessionStorage.setItem("geo_redirected", "true");
        }
        router.replace(`/${code}`);
      } catch (e) {
        // Silently fail if storage is blocked
        console.warn("Geo-redirect failed:", e);
      }
    };

    // Check localStorage first (with error handling for bots/iframes)
    try {
      const savedCountry = localStorage.getItem(STORAGE_KEY);
      if (savedCountry && SUPPORTED_COUNTRIES[savedCountry.toUpperCase()]) {
        performRedirect(SUPPORTED_COUNTRIES[savedCountry.toUpperCase()]);
        return;
      }
      if (savedCountry) {
        const allCodes = Object.values(SUPPORTED_COUNTRIES);
        if (allCodes.includes(savedCountry.toLowerCase())) {
          performRedirect(savedCountry.toLowerCase());
          return;
        }
      }
    } catch (e) {
      // localStorage blocked in iframe or by bot - continue without redirect
      console.warn("localStorage access blocked:", e);
      return;
    }

    // No saved preference — call geolocation API
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);

    fetch(GEO_API_URL, { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error("Geo API error");
        return res.json();
      })
      .then((data) => {
        const isoCode = (data.country_code || data.country || "").toUpperCase();
        const mapped = SUPPORTED_COUNTRIES[isoCode];
        if (mapped) {
          try {
            localStorage.setItem(STORAGE_KEY, mapped);
          } catch (e) {
            // localStorage blocked - continue without saving
          }
          performRedirect(mapped);
        }
      })
      .catch(() => {
        // API failed or timed out — user picks manually
      })
      .finally(() => {
        clearTimeout(timeout);
      });

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [pathname, router]);
}

/**
 * Call this when the user manually clicks a country card.
 * Saves their preference so geo-redirect respects it next time.
 */
export function saveCountryPreference(countryCode: string) {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, countryCode.toLowerCase());
  }
}
